'use client';
import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Heart, Wind, Bone, Brain, Baby, Stethoscope, Ribbon,
  Thermometer, ScanLine, Activity, UserRound, ClipboardList,
  Building2, ArrowLeft, RotateCcw, ChevronRight, Phone, MapPin, MessageCircle
} from 'lucide-react';
import TopNav from '@/components/layout/TopNav';
import MedicalDisclaimer from '@/components/ui/MedicalDisclaimer';
import MedicalVisual from '@/components/ui/MedicalVisual';
import { careConcerns, careQuestions, primaryNeeds } from '@/data/careJourney';
import type { JourneyAnswers, PrimaryNeed } from '@/data/careJourney';
import { trackEvent } from '@/lib/analytics';
import { hospitalConfig } from '@/data/hospitalConfig';
import Icon from '@/components/ui/AppIcon';


type JourneyStep = 0 | 1 | 2 | 3 | 4 | 5;

// Icon map for concerns — replaces emojis
const concernIconMap: Record<string, React.ElementType> = {
  'heart-chest': Heart,
  'breathing': Wind,
  'bones-joints': Bone,
  'brain-nervous': Brain,
  'womens-health': UserRound,
  'child-health': Baby,
  'diabetes': Activity,
  'cancer': Ribbon,
  'fever-infection': Thermometer,
  'other': ScanLine,
};

// Icon map for primary needs
const needIconMap: Record<string, React.ElementType> = {
  'health-concern': Stethoscope,
  'find-doctor': UserRound,
  'health-checkup': ClipboardList,
  'follow-up': Activity,
  'specific-department': Building2,
};

const concernColorMap: Record<string, string> = {
  'heart-chest': 'text-rose-400',
  'breathing': 'text-sky-400',
  'bones-joints': 'text-slate-300',
  'brain-nervous': 'text-indigo-400',
  'womens-health': 'text-pink-400',
  'child-health': 'text-emerald-400',
  'diabetes': 'text-amber-400',
  'cancer': 'text-purple-400',
  'fever-infection': 'text-orange-400',
  'other': 'text-primary',
};

// Framer Motion variants
const panelVariants = {
  enter: { opacity: 0, x: 30, scale: 0.98 },
  center: { opacity: 1, x: 0, scale: 1 },
  exit: { opacity: 0, x: -30, scale: 0.98 },
};

const visualVariants = {
  enter: { opacity: 0, scale: 0.85, rotate: -5 },
  center: { opacity: 1, scale: 1, rotate: 0 },
  exit: { opacity: 0, scale: 0.85, rotate: 5 },
};

const cardVariants = {
  hidden: { opacity: 0, y: 16 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.07, duration: 0.4, ease: 'easeOut' },
  }),
};

// Determine which visual to show based on step/answers
function getActiveVisual(step: JourneyStep, answers: JourneyAnswers): string {
  if (step >= 2 && answers.concern) return answers.concern;
  if (step === 1 && answers.primaryNeed) return answers.primaryNeed;
  return 'default';
}

export default function CareJourneyContent() {
  const router = useRouter();
  const searchParams = useSearchParams();

  // Determine initial step and answers from query params
  // ?start=health-concern → skip intro + primary-need, go straight to concern selection (step 2)
  const startParam = searchParams.get('start');
  const initialStep: JourneyStep = startParam === 'health-concern' ? 2 : 0;
  const initialAnswers: JourneyAnswers = startParam === 'health-concern'
    ? { primaryNeed: 'health-concern' }
    : {};

  const [step, setStep] = useState<JourneyStep>(initialStep);
  const [answers, setAnswers] = useState<JourneyAnswers>(initialAnswers);
  const [transitioning, setTransitioning] = useState(false);
  const [showEmergencyOverlay, setShowEmergencyOverlay] = useState(false);
  const [selectedOption, setSelectedOption] = useState<string | null>(null);
  const [direction, setDirection] = useState<1 | -1>(1);
  const mainRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    trackEvent({ event: 'journey_started', source: 'care-journey-screen' });
  }, []);

  useEffect(() => {
    mainRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  }, [step]);

  const transitionTo = useCallback(
    (nextStep: JourneyStep, dir: 1 | -1 = 1, callback?: () => void) => {
      if (transitioning) return;
      setTransitioning(true);
      setDirection(dir);
      setTimeout(() => {
        callback?.();
        setStep(nextStep);
        setSelectedOption(null);
        setTransitioning(false);
      }, 50);
    },
    [transitioning]
  );

  const handleBack = useCallback(() => {
    if (transitioning) return;
    if (step === 0) return;
    // If we started directly at step 2 (health-concern shortcut), BACK goes to reception
    if (step === 2 && startParam === 'health-concern') {
      router.push('/digital-reception-screen');
      return;
    }
    if (step === 1) transitionTo(0, -1);
    else if (step === 2) transitionTo(1, -1);
    else if (step === 3) {
      if (answers.primaryNeed === 'health-concern') transitionTo(2, -1);
      else transitionTo(1, -1);
    } else transitionTo((step - 1) as JourneyStep, -1);
  }, [step, transitioning, answers.primaryNeed, transitionTo, startParam, router]);

  const handleRestart = useCallback(() => {
    if (transitioning) return;
    setTransitioning(true);
    setTimeout(() => {
      setAnswers({});
      setStep(1);
      setSelectedOption(null);
      setTransitioning(false);
    }, 50);
  }, [transitioning]);

  const handleBegin = useCallback(() => {
    // LET'S BEGIN → advances from intro (step 0) to primary need selection (step 1)
    transitionTo(1, 1);
  }, [transitionTo]);

  const handlePrimaryNeedSelect = useCallback(
    (need: PrimaryNeed) => {
      if (transitioning) return;
      setSelectedOption(need.id);
      trackEvent({ event: 'primary_need_selected', source: need.id });

      setTimeout(() => {
        if (need.flowType === 'health-concern') {
          transitionTo(2, 1, () => setAnswers((prev) => ({ ...prev, primaryNeed: need.id })));
        } else if (need.flowType === 'find-doctor') {
          setAnswers((prev) => ({ ...prev, primaryNeed: need.id }));
          trackEvent({ event: 'doctor_journey_started', source: 'primary-need' });
          router.push('/find-doctor');
        } else if (need.flowType === 'health-checkup') {
          setAnswers((prev) => ({ ...prev, primaryNeed: need.id }));
          trackEvent({ event: 'health_checkup_started', source: 'primary-need' });
          router.push('/health-checkup');
        } else if (need.flowType === 'follow-up') {
          setAnswers((prev) => ({ ...prev, primaryNeed: need.id }));
          router.push('/appointment-booking-screen');
        } else {
          setAnswers((prev) => ({ ...prev, primaryNeed: need.id }));
          router.push('/find-doctor');
        }
      }, 180);
    },
    [transitioning, transitionTo, router]
  );

  const handleConcernSelect = useCallback(
    (concernId: string) => {
      if (transitioning) return;
      setSelectedOption(concernId);
      trackEvent({ event: 'health_concern_selected', source: concernId });
      trackEvent({ event: 'concern_selected' });
      setTimeout(() => {
        transitionTo(3, 1, () => setAnswers((prev) => ({ ...prev, concern: concernId })));
      }, 180);
    },
    [transitioning, transitionTo]
  );

  const handleAnswerSelect = useCallback(
    (questionStep: JourneyStep, value: string) => {
      if (transitioning) return;
      setSelectedOption(value);
      trackEvent({ event: 'question_answered', step: questionStep });

      const newAnswers = { ...answers };
      if (questionStep === 3) newAnswers.duration = value;
      if (questionStep === 4) newAnswers.severity = value;
      if (questionStep === 5) newAnswers.priorConsultation = value;

      setTimeout(() => {
        const concern = careConcerns.find((c) => c.id === newAnswers.concern);
        if (
          questionStep === 4 &&
          value === 'severe' &&
          newAnswers.duration === 'today' &&
          concern?.emergencyRisk
        ) {
          setAnswers(newAnswers);
          trackEvent({ event: 'emergency_triggered', source: 'care-journey' });
          setShowEmergencyOverlay(true);
          return;
        }

        if (questionStep === 5) {
          setAnswers(newAnswers);
          const params = new URLSearchParams({
            concern: newAnswers.concern ?? '',
            duration: newAnswers.duration ?? '',
            severity: newAnswers.severity ?? '',
            priorConsultation: newAnswers.priorConsultation ?? '',
          });
          trackEvent({ event: 'journey_completed', source: 'care-journey' });
          trackEvent({ event: 'care_recommendation_shown', departmentId: newAnswers.concern });
          router.push(`/care-recommendation-screen?${params.toString()}`);
          return;
        }

        transitionTo((questionStep + 1) as JourneyStep, 1, () => setAnswers(newAnswers));
      }, 180);
    },
    [answers, transitioning, transitionTo, router]
  );

  const progressStep = step === 0 ? 1 : step === 1 ? 1 : step === 2 ? 2 : step;
  const currentQuestion = careQuestions.find((q) => q.step === step);
  const selectedConcern = careConcerns.find((c) => c.id === answers.concern);
  const activeVisual = getActiveVisual(step, answers);

  const animVariants = {
    enter: { opacity: 0, x: direction * 40, scale: 0.98 },
    center: { opacity: 1, x: 0, scale: 1 },
    exit: { opacity: 0, x: direction * -40, scale: 0.98 },
  };

  return (
    <main
      ref={mainRef}
      className="journey-bg min-h-screen flex flex-col pb-24 md:pb-12 overflow-x-hidden"
      role="main"
      aria-label="My Care Journey — Guided care navigation"
    >

      <TopNav
        showBackButton={step > 0 || startParam === 'health-concern'}
        backHref={step === 0 ? '/digital-reception-screen' : '/digital-reception-screen'}
        backLabel={step === 0 ? 'Reception' : 'Back'}
      />

      {/* Emergency overlay */}
      <AnimatePresence>
        {showEmergencyOverlay && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 backdrop-blur-md px-4"
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="emergency-overlay-title"
          >
            <motion.div
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              className="bg-red-950 border border-red-700 rounded-2xl p-8 max-w-md w-full text-center shadow-2xl"
            >
              <div className="w-16 h-16 rounded-full bg-red-900/60 border border-red-600 flex items-center justify-center mx-auto mb-5 animate-emergency-pulse">
                <Phone size={28} className="text-red-300" aria-hidden="true" />
              </div>
              <h2 id="emergency-overlay-title" className="text-xl font-bold text-red-300 mb-3 tracking-wide leading-snug">
                THIS MAY REQUIRE IMMEDIATE MEDICAL ATTENTION
              </h2>
              <p className="text-sm text-red-200/80 mb-2 leading-relaxed">
                Based on what you&apos;ve shared, these symptoms may require urgent evaluation.
              </p>
              <p className="text-xs text-red-300/60 mb-7 leading-relaxed">
                If you believe this is a medical emergency, seek immediate emergency medical care. Do not wait for online guidance.
              </p>
              <div className="flex flex-col gap-3">
                <a
                  href={hospitalConfig.emergencyTel}
                  onClick={() => trackEvent({ event: 'call_clicked', source: 'emergency-overlay' })}
                  className="btn-emergency w-full text-sm tracking-widest flex items-center justify-center gap-3"
                  aria-label={`Call All Is Well Hospital Emergency: ${hospitalConfig.emergency}`}
                >
                  <Phone size={18} aria-hidden="true" />
                  CALL EMERGENCY NOW — {hospitalConfig.emergency}
                </a>
                <a
                  href={hospitalConfig.maps}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full rounded-xl border border-red-700 text-red-300 hover:bg-red-900/30 transition-all duration-200 py-4 text-sm font-bold tracking-widest flex items-center justify-center gap-2 min-h-[52px]"
                  aria-label="Get directions to All Is Well Hospital"
                >
                  <MapPin size={16} aria-hidden="true" />
                  GET DIRECTIONS
                </a>
                <a
                  href={hospitalConfig.whatsapp}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => trackEvent({ event: 'whatsapp_clicked', source: 'emergency-overlay' })}
                  className="w-full rounded-xl border border-red-800/50 text-red-400 hover:bg-red-900/20 transition-all duration-200 py-4 text-sm font-bold tracking-widest flex items-center justify-center gap-2 min-h-[52px]"
                  aria-label="WhatsApp All Is Well Hospital"
                >
                  <MessageCircle size={16} aria-hidden="true" />
                  WHATSAPP
                </a>
                <button
                  onClick={() => setShowEmergencyOverlay(false)}
                  className="text-xs text-muted-foreground hover:text-foreground transition-colors mt-1 underline"
                  aria-label="Continue with care journey"
                >
                  Continue with care journey anyway
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="flex-1 flex flex-col items-center justify-start pt-20 px-4 md:px-8">
        <div className="w-full max-w-6xl">

          {/* Back + Progress bar */}
          {(step >= 1 || startParam === 'health-concern') && (
            <div className="flex items-center justify-between mb-6 max-w-xl mx-auto md:mx-0">
              {(step > 1 || (step === 2 && startParam === 'health-concern')) ? (
                <button
                  onClick={handleBack}
                  className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors text-sm font-medium focus-gold rounded"
                  aria-label="Go to previous step"
                >
                  <ArrowLeft size={16} aria-hidden="true" />
                  <span>BACK</span>
                </button>
              ) : (
                <div />
              )}
              <div className="flex items-center gap-4">
                <ElegantProgress currentStep={progressStep} />
                {step > 1 && (
                  <button
                    onClick={handleRestart}
                    className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
                    aria-label="Restart care journey"
                  >
                    <RotateCcw size={12} aria-hidden="true" />
                    <span>Start Again</span>
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Two-column layout: Content LEFT, Visual RIGHT */}
          <div className="flex flex-col md:flex-row md:items-center md:gap-12 lg:gap-20">

            {/* LEFT: Question content */}
            <div className="flex-1 min-w-0 max-w-xl mx-auto md:mx-0 w-full">
              <AnimatePresence mode="wait" custom={direction}>
                <motion.div
                  key={`step-${step}`}
                  custom={direction}
                  variants={animVariants}
                  initial="enter"
                  animate="center"
                  exit="exit"
                  transition={{ duration: 0.4, ease: [0.25, 0.46, 0.45, 0.94] }}
                >

                  {/* ── STEP 0: START (only shown when navigating directly to /care-journey-screen without a start param) ── */}
                  {step === 0 && (
                    <div className="text-center py-8 md:text-left md:py-12">
                      <p className="section-label mb-5">MY CARE JOURNEY™</p>
                      <h1 className="text-display-md text-white font-bold mb-4 leading-tight">
                        LET&apos;S START<br />
                        <span className="gold-gradient-text">WITH YOU.</span>
                      </h1>
                      <p className="text-base text-muted-foreground mb-3 leading-relaxed max-w-sm mx-auto md:mx-0">
                        Tell us a little about what brings you to All Is Well.
                      </p>
                      <p className="text-sm text-primary/70 mb-10 tracking-wide">
                        We&apos;ll guide you one step at a time.
                      </p>
                      <button
                        onClick={handleBegin}
                        className="btn-primary text-sm px-12 py-4 tracking-widest w-full max-w-xs mx-auto md:mx-0 block"
                        aria-label="Begin your care journey"
                      >
                        LET&apos;S BEGIN →
                      </button>
                      <div className="mt-10">
                        <MedicalDisclaimer compact />
                      </div>
                    </div>
                  )}

                  {/* ── STEP 1: PRIMARY NEED ── */}
                  {step === 1 && (
                    <div>
                      <div className="mb-7">
                        <p className="section-label mb-3">MY CARE JOURNEY™</p>
                        <h1 className="text-display-md text-white font-bold mb-2 leading-tight">
                          WHAT BRINGS YOU TO<br />
                          <span className="gold-gradient-text">ALL IS WELL</span> TODAY?
                        </h1>
                      </div>
                      <div className="flex flex-col gap-3" role="radiogroup" aria-label="Select your primary need">
                        {primaryNeeds.map((need, idx) => {
                          const NeedIcon = needIconMap[need.id] ?? Stethoscope;
                          return (
                            <motion.button
                              key={need.id}
                              custom={idx}
                              variants={cardVariants}
                              initial="hidden"
                              animate="visible"
                              onClick={() => handlePrimaryNeedSelect(need)}
                              whileHover={{ scale: 1.01, y: -2 }}
                              whileTap={{ scale: 0.99 }}
                              className={`premium-option-card group p-5 flex items-center gap-4 text-left w-full ${
                                selectedOption === need.id ? 'selected' : ''
                              }`}
                              role="radio"
                              aria-checked={selectedOption === need.id}
                              aria-label={need.label}
                            >
                              <div className="option-icon-wrap flex-shrink-0">
                                <NeedIcon size={20} className="text-primary" aria-hidden="true" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <span className="block font-bold text-sm tracking-widest text-foreground uppercase leading-snug">
                                  {need.label}
                                </span>
                                <span className="block text-xs text-muted-foreground mt-0.5">{need.sublabel}</span>
                              </div>
                              <ChevronRight size={16} className="text-muted-foreground group-hover:text-primary transition-colors flex-shrink-0" aria-hidden="true" />
                            </motion.button>
                          );
                        })}
                      </div>
                      <div className="mt-6">
                        <MedicalDisclaimer compact />
                      </div>
                    </div>
                  )}

                  {/* ── STEP 2: CONCERN SELECTION ── */}
                  {step === 2 && (
                    <div>
                      <div className="mb-7">
                        <p className="section-label mb-3">MY CARE JOURNEY™</p>
                        <h1 className="text-display-md text-white font-bold mb-2 leading-tight">
                          WHAT&apos;S<br />
                          <span className="gold-gradient-text">BOTHERING YOU?</span>
                        </h1>
                        <p className="text-sm text-muted-foreground mt-2">
                          Tell us what you&apos;re experiencing. You don&apos;t need to know which doctor you need.
                        </p>
                      </div>
                      <div
                        className="grid grid-cols-2 gap-3 mb-6"
                        role="radiogroup"
                        aria-label="Select your health concern"
                      >
                        {careConcerns.map((concern, idx) => {
                          const ConcernIcon = concernIconMap[concern.id] ?? Stethoscope;
                          const iconColor = concernColorMap[concern.id] ?? 'text-primary';
                          return (
                            <motion.button
                              key={`concern-${concern.id}`}
                              custom={idx}
                              variants={cardVariants}
                              initial="hidden"
                              animate="visible"
                              onClick={() => handleConcernSelect(concern.id)}
                              whileHover={{ scale: 1.02, y: -2 }}
                              whileTap={{ scale: 0.98 }}
                              className={`premium-concern-card group ${
                                selectedOption === concern.id ? 'selected' : ''
                              }`}
                              role="radio"
                              aria-checked={selectedOption === concern.id}
                              aria-label={concern.label}
                            >
                              <div className={`concern-icon-wrap ${iconColor}`}>
                                <ConcernIcon size={22} aria-hidden="true" />
                              </div>
                              <span className="text-xs font-semibold text-foreground leading-snug text-center">
                                {concern.label}
                              </span>
                            </motion.button>
                          );
                        })}
                      </div>
                      <MedicalDisclaimer compact />
                    </div>
                  )}

                  {/* ── STEPS 3–5: QUESTIONS ── */}
                  {step >= 3 && step <= 5 && currentQuestion && (
                    <div>
                      {/* Concern context chip */}
                      {selectedConcern && (
                        <motion.div
                          initial={{ opacity: 0, y: -8 }}
                          animate={{ opacity: 1, y: 0 }}
                          className="flex items-center gap-2 mb-6"
                        >
                          {(() => {
                            const ConcernChipIcon = concernIconMap[selectedConcern.id] ?? Stethoscope;
                            const iconColor = concernColorMap[selectedConcern.id] ?? 'text-primary';
                            return (
                              <>
                                <ConcernChipIcon size={16} className={iconColor} aria-hidden="true" />
                                <span className="text-sm text-primary font-medium">{selectedConcern.label}</span>
                              </>
                            );
                          })()}
                        </motion.div>
                      )}

                      {/* Question card */}
                      <div className="premium-question-card mb-4" role="group" aria-labelledby={`question-${currentQuestion.id}`}>
                        <h2
                          id={`question-${currentQuestion.id}`}
                          className="text-xl md:text-2xl font-bold tracking-wide mb-2 leading-snug text-white"
                        >
                          {currentQuestion.question}
                        </h2>
                        {currentQuestion.subtitle && (
                          <p className="text-sm text-muted-foreground mb-6">{currentQuestion.subtitle}</p>
                        )}
                        <div className="flex flex-col gap-3 mt-6" role="radiogroup" aria-label={currentQuestion.question}>
                          {currentQuestion.options.map((option, idx) => {
                            const currentAnswer =
                              step === 3 ? answers.duration :
                              step === 4 ? answers.severity :
                              answers.priorConsultation;
                            const isSelected = currentAnswer === option.value || selectedOption === option.value;
                            return (
                              <motion.button
                                key={option.id}
                                custom={idx}
                                variants={cardVariants}
                                initial="hidden"
                                animate="visible"
                                onClick={() => handleAnswerSelect(step, option.value)}
                                whileHover={{ scale: 1.01 }}
                                whileTap={{ scale: 0.98 }}
                                className={`premium-answer-option ${isSelected ? 'selected' : ''}`}
                                role="radio"
                                aria-checked={isSelected}
                                aria-label={option.label}
                              >
                                <span className="flex items-center gap-3">
                                  <span
                                    className={`w-4 h-4 rounded-full border-2 flex-shrink-0 transition-all duration-200 ${
                                      isSelected ? 'border-primary bg-primary' : 'border-muted-foreground'
                                    }`}
                                    aria-hidden="true"
                                  />
                                  {option.label}
                                </span>
                              </motion.button>
                            );
                          })}
                        </div>
                      </div>

                      <div className="mt-4">
                        <MedicalDisclaimer compact />
                      </div>
                    </div>
                  )}

                </motion.div>
              </AnimatePresence>
            </div>

            {/* RIGHT: 3D Medical Visual — hidden on mobile step 0 */}
            {step !== 0 && (
              <div className="hidden md:flex md:flex-shrink-0 md:w-72 lg:w-80 xl:w-96 items-center justify-center">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={`visual-${activeVisual}`}
                    variants={visualVariants}
                    initial="enter"
                    animate="center"
                    exit="exit"
                    transition={{ duration: 0.5, ease: 'easeOut' }}
                    className="visual-float"
                  >
                    <MedicalVisual concern={activeVisual} className="w-64 h-64 lg:w-72 lg:h-72 xl:w-80 xl:h-80" />
                  </motion.div>
                </AnimatePresence>
              </div>
            )}

            {/* Mobile visual — shown below content on mobile (steps 2+) */}
            {step >= 2 && (
              <div className="flex md:hidden justify-center mt-6 mb-2">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={`visual-mobile-${activeVisual}`}
                    variants={visualVariants}
                    initial="enter"
                    animate="center"
                    exit="exit"
                    transition={{ duration: 0.5, ease: 'easeOut' }}
                    className="visual-float"
                  >
                    <MedicalVisual concern={activeVisual} className="w-40 h-40" />
                  </motion.div>
                </AnimatePresence>
              </div>
            )}

          </div>
        </div>
      </div>
    </main>
  );
}

// Elegant step progress indicator
function ElegantProgress({ currentStep }: { currentStep: number }) {
  const total = 5;
  const stepLabels = ['Start', 'Concern', 'Duration', 'Severity', 'History'];

  return (
    <div
      className="flex items-center gap-3"
      role="progressbar"
      aria-valuenow={currentStep}
      aria-valuemin={1}
      aria-valuemax={total}
      aria-label={`Step ${currentStep} of ${total}`}
    >
      <div className="flex items-center gap-1.5" aria-hidden="true">
        {Array.from({ length: total }).map((_, i) => {
          const isDone = i + 1 < currentStep;
          const isCurrent = i + 1 === currentStep;
          return (
            <React.Fragment key={`prog-${i}`}>
              <motion.div
                animate={{
                  width: isCurrent ? 24 : isDone ? 16 : 8,
                  backgroundColor: isDone || isCurrent ? 'var(--primary)' : 'var(--muted)',
                  opacity: isDone ? 0.6 : isCurrent ? 1 : 0.4,
                }}
                transition={{ duration: 0.35, ease: 'easeOut' }}
                className="h-1.5 rounded-full"
              />
              {i < total - 1 && (
                <div className={`h-px w-3 transition-all duration-300 ${isDone ? 'bg-primary/40' : 'bg-border'}`} />
              )}
            </React.Fragment>
          );
        })}
      </div>
      <span className="text-xs text-muted-foreground font-tabular tracking-widest">
        {String(currentStep).padStart(2, '0')} / {String(total).padStart(2, '0')}
      </span>
    </div>
  );
}